import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
// @ts-ignore
import { Component, OnInit, OnDestroy } from '@angular/core';
// @ts-ignore
import { CommonModule } from '@angular/common';
// @ts-ignore
import { FormsModule } from '@angular/forms';
// @ts-ignore
import { firstValueFrom } from 'rxjs';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { SerialService } from '../../services/serial.service';
import { LoteService } from '../../services/lote.service';

interface RazaoBloqueio {
  observacao: string;
  vlrLimite: number;
  vlrAtual: number;
  sequencia: number;
  nurng: number;
  // Novos campos para o payload de aprovação nativa
  evento: number;
  nucll: number;
  seqCascata: number;
  tabela: string;
}

interface GrupoEvento {
  evento: number;
  descricao: string;
  razoes: RazaoBloqueio[];
  aberto: boolean;
}

interface LiberacaoItem {
  grupos?: GrupoEvento[];
  nunota: number;
  codemp: number;
  codTipOper: number;
  dtmov: string;
  cliente: string;
  docparc: string;
  vendedor: string;
  vlrnota: number;
  vlrmoeda: number;
  empresa: string;
  aeronave: string;
  razoes: RazaoBloqueio[];
  expandido?: boolean;
  evento: string;
}

// @ts-ignore
@Component({
  selector: 'app-liberacao-lista',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './liberacao-lista.component.html',
  styleUrl: './liberacao-lista.component.css'
})
export class LiberacaoListaComponent implements OnInit, OnDestroy {

  itens: LiberacaoItem[] = [];
  carregando = false;
  erro: string | null = null;
  meuCodUsu: number = 0;

  // Estado de ação em andamento por nunota
  aprovando: number | null = null;
  reprovando: number | null = null;

  // Modal de reprovação
  modalReprovacao: number | null = null;
  justificativa = '';

  // Toast
  toastVisivel = false;
  toastMensagem = '';
  private toastTimer: any = null;

  // Auto-refresh
  private refreshTimer: any = null;

  constructor(
    private sankhyaGateway: SankhyaGatewayService,
    private serialService: SerialService,
    private loteService: LoteService
  ) { }

  ngOnInit(): void {
    this.carregar();
    // Atualiza automaticamente a cada 10 segundos
    this.refreshTimer = setInterval(() => this.carregar(false), 10000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  async carregar(mostrarLoader = true): Promise<void> {
    if (mostrarLoader) this.carregando = true;
    this.erro = null;
    try {
      const codUsu = await firstValueFrom(this.sankhyaGateway.buscarMeuCodusu());
      this.meuCodUsu = codUsu.codusu ?? 0;
      const res: any = await firstValueFrom(this.sankhyaGateway.listarLiberacoesPendentes(codUsu.codusu));
      if (res?.status === '1' || res?.status === 1) {
        const body = res?.responseBody || {};
        // Dependendo de como o proxy entrega, pode vir em result ou entities
        const rawLista = body.result || body.entities?.entity || body.entities || [];

        // Converte e agrupa as linhas nativas por NUNOTA
        this.itens = this.agruparLiberacoesNativas(Array.isArray(rawLista) ? rawLista : [rawLista]);
      } else {
        this.erro = res?.statusMessage || res?.responseBody?.erro || 'Erro ao carregar liberações.';
      }
    } catch (err: any) {
      this.erro = err?.message ?? 'Erro de comunicação.';
    } finally {
      this.carregando = false;
    }
  }

  /**
   * Lê o retorno bruto do Sankhya (onde cada bloqueio é uma linha)
   * e converte para o formato esperado pela tela (1 Card por NUNOTA com array de motivos).
   */
  private agruparLiberacoesNativas(linhas: any[]): LiberacaoItem[] {
    const mapa = new Map<number, LiberacaoItem>();

    // Função auxiliar para lidar com o formato nativo do Sankhya { $: valor } ou valores diretos
    const getVal = (linha: any, campo: string) => linha[campo]?.['$'] ?? linha[campo] ?? '';

    for (const linha of linhas) {
      if (!linha) continue;

      const nunota = Number(linha[2] || getVal(linha, 'NUNOTA'));
      if (!nunota) continue;

      if (!mapa.has(nunota)) {
        // Formata data de DD/MM/YYYY para string legível, se necessário, ou mantém a original
        const dtneg = linha[29] || getVal(linha, 'DTNEG');

        mapa.set(nunota, {
          nunota: nunota,
          codemp: Number(linha[22] || getVal(linha, 'CODEMP')),
          codTipOper: Number(linha[30] || getVal(linha, 'CODTIPOPER')),
          dtmov: dtneg,
          cliente: linha[21] || getVal(linha, 'Parceiro.NOMEPARC'),
          docparc: linha[60] || getVal(linha, 'Parceiro.CGC_CPF') || '', // NOTA: Veja aviso abaixo
          vendedor: linha[25] || getVal(linha, 'Vendedor.APELIDO'),
          vlrnota: Number(linha[32] || getVal(linha, 'VLRNOTA')),
          vlrmoeda: Number(linha[44] || getVal(linha, 'VLRTOTAL')),
          empresa: linha[23] || getVal(linha, 'Empresa.NOMEFANTASIA'),
          aeronave: '', // NOTA: Adicionar campo no payload se necessário
          razoes: [],
          expandido: false,
          evento: linha[3]
        });
      }

      // Adiciona o motivo do bloqueio
      const item = mapa.get(nunota)!;
      const descricaoEvento = linha[4] || getVal(linha, 'ViewEventoLiberacao.DESCRICAO');
      const seq = Number(linha[17] || getVal(linha, 'SEQUENCIA'));

      // O Sankhya pode gerar linhas vazias se for apenas o cabeçalho, então validamos:
      if (descricaoEvento) {
        item.razoes.push({
          observacao: descricaoEvento,
          vlrLimite: Number(linha[9] || getVal(linha, 'VLRLIMITE')),
          vlrAtual: Number(linha[10] || getVal(linha, 'VLRATUAL')),
          sequencia: seq,
          nurng: Number(linha[42] || getVal(linha, 'NURNG')),
          evento: Number(linha[3] || getVal(linha, 'EVENTO') || 0),
          nucll: Number(linha[53] || getVal(linha, 'NUCLL') || 0),
          seqCascata: Number(linha[51] || getVal(linha, 'SEQCASCATA') || 0),
          tabela: linha[43] || getVal(linha, 'TABELA')
        });
      }
    }

    const lista = Array.from(mapa.values());
    lista.forEach(i => this.montarGrupos(i));
    return lista;
  }

  /** Agrupa os bloqueios do pedido por evento: uma linha por evento (e não uma por item). */
  private montarGrupos(item: LiberacaoItem): void {
    const abertos = new Set((item.grupos ?? []).filter(g => g.aberto).map(g => g.evento));
    const mapa = new Map<number, GrupoEvento>();
    for (const r of item.razoes) {
      let g = mapa.get(r.evento);
      if (!g) { g = { evento: r.evento, descricao: r.observacao, razoes: [], aberto: abertos.has(r.evento) }; mapa.set(r.evento, g); }
      g.razoes.push(r);
    }
    item.grupos = Array.from(mapa.values());
  }

  // Itens do pedido (seq → PN/descrição/qtd), carregados sob demanda ao abrir "ver itens".
  detalhesItens: Record<number, { carregando: boolean; erro?: string; mapa: Record<number, { pn: string; descricao: string; qtd: number }> }> = {};

  async alternarGrupo(item: LiberacaoItem, g: GrupoEvento): Promise<void> {
    g.aberto = !g.aberto;
    if (!g.aberto || this.detalhesItens[item.nunota]) return;
    const det = { carregando: true, mapa: {} as Record<number, { pn: string; descricao: string; qtd: number }>, erro: undefined as string | undefined };
    this.detalhesItens[item.nunota] = det;
    try {
      const res: any = await firstValueFrom(this.sankhyaGateway.buscarDetalhesPedidoVenda(item.nunota));
      const body = res?.responseBody ?? res;
      const d = body?.sucesso ? (body.detalhe ?? body) : null;
      for (const i of (d?.itens ?? [])) {
        det.mapa[Number(i.seq)] = { pn: i.refforn ?? '', descricao: i.descricao ?? '', qtd: Number(i.qtd ?? 0) || 0 };
      }
      if (!d) det.erro = 'Não foi possível carregar os itens.';
    } catch (e: any) {
      det.erro = e?.message ?? 'Erro ao carregar os itens.';
    } finally {
      det.carregando = false;
    }
  }

  itemDoGrupo(nunota: number, r: RazaoBloqueio): { pn: string; descricao: string; qtd: number } | null {
    return this.detalhesItens[nunota]?.mapa?.[r.sequencia] ?? null;
  }

  toggleExpandir(item: LiberacaoItem): void {
    item.expandido = !item.expandido;
  }

  async aprovar(nunota: number, razao?: RazaoBloqueio | RazaoBloqueio[]): Promise<void> {
    if (this.aprovando === nunota) return;
    this.aprovando = nunota;

    try {
      const item = this.itens.find(i => i.nunota === nunota);
      if (!item || !item.razoes || item.razoes.length === 0) {
        this.mostrarToast('Não foram encontrados bloqueios válidos para esta nota.');
        this.aprovando = null;
        return;
      }

      // Monta o array dinâmico usando as informações que salvamos no agruparLiberacoesNativas
      // Com `razao`: libera só aquele evento; sem ela: todos os restantes da nota.
      const alvo = razao ? (Array.isArray(razao) ? razao : [razao]) : item.razoes;
      const itensLiberacao = alvo.map(r => ({
        nuChave: nunota,
        evento: r.evento,
        nucll: r.nucll,
        seqCascata: r.seqCascata,
        sequencia: r.sequencia,
        tabela: r.tabela
      }));

      // 1. Envia a aprovação (Passando o meuCodUsu que foi carregado no Início)
      const resLib: any = await firstValueFrom(this.sankhyaGateway.aprovarLiberacaoNativa(this.meuCodUsu, itensLiberacao));
      const bodyLib = resLib?.responseBody ?? resLib;
      if ((resLib?.status === '1' || resLib?.status === 1) && Number(item.codTipOper) === 1000) {
        // Pré-Venda (TOP 1000): a liberação é por departamento. Só autoriza/confirma/gera a Venda quando NÃO restar nenhuma liberação pendente.
        try {
          const v: any = await firstValueFrom(this.sankhyaGateway.verificarLiberacoesPreVenda(nunota));
          if (v?.autorizado) {
            this.mostrarToast(`✅ Pré-Venda #${nunota}: todas as liberações concluídas — autorizada e Venda gerada (se houver itens escolhidos).`, 9000);
          } else if (v?.erro) {
            this.mostrarToast(`⚠️ Pré-Venda #${nunota} liberada neste departamento, mas a autorização falhou: ${v.erro}`, 12000);
          } else if (v?.novaLiberacao) {
            const prox = (v?.eventos ?? []).find((e: any) => e.status === 'PENDENTE');
            this.mostrarToast(`✅ Liberação registrada. Próxima liberação da Pré-Venda #${nunota}: ${prox?.descricao ?? 'pendente'}.`, 9000);
          } else if (Number(v?.eventosPendentes ?? 0) > 0) {
            this.mostrarToast(`✅ Liberação registrada. A Pré-Venda #${nunota} ainda aguarda ${v.eventosPendentes} liberação(ões) de outro(s) departamento(s).`, 9000);
          } else if (Number(v?.eventosReprovados ?? 0) > 0) {
            this.mostrarToast(`⚠️ Pré-Venda #${nunota} possui liberação reprovada.`, 9000);
          } else {
            this.mostrarToast(`✅ Liberação registrada na Pré-Venda #${nunota}.`, 7000);
          }
        } catch (errV: any) {
          this.mostrarToast(`⚠️ Pré-Venda #${nunota} liberada, mas não consegui verificar as demais liberações: ${errV?.error?.erro ?? errV?.message ?? ''}`, 10000);
        }
        this.removerRazaoOuItem(nunota, razao);
      } else if (resLib?.status === '1' || resLib?.status === 1) {
        // Só confirma/fatura na ÚLTIMA liberação e se NÃO houver reprovação: consulta o estado real das liberações da nota
        // (inclui eventos de outros usuários, que não aparecem nesta tela).
        try {
          const lr: any = await firstValueFrom(this.sankhyaGateway.chamarServiceGpl('PedidoVendaGlobalSP.doListarLiberacoesNota', { nunota }) as any);
          const lb = lr?.responseBody ?? lr;
          if (Number(lb?.eventosReprovados ?? 0) > 0) {
            this.removerRazaoOuItem(nunota);
            this.mostrarToast(`🚫 Pedido #${nunota} possui liberação REPROVADA — não foi confirmado nem faturado.`, 10000);
            return;
          }
          if (Number(lb?.eventosPendentes ?? 0) > 0) {
            this.removerRazaoOuItem(nunota, razao);
            this.mostrarToast(`✅ Evento liberado no pedido #${nunota}. Ainda aguarda ${lb.eventosPendentes} liberação(ões) — a confirmação só ocorre na última.`, 9000);
            return;
          }
        } catch (eLib: any) {
          this.mostrarToast(`⚠️ Liberação registrada em #${nunota}, mas não consegui verificar as demais liberações; a nota NÃO foi confirmada: ${eLib?.message ?? ''}`, 12000);
          return;
        }
        // 2. Confirma a Nota no Sankhya
        try {
          const resConf: any = await firstValueFrom(this.sankhyaGateway.confirmarNotasNativa(nunota));
          if (resConf?.status === '1' || resConf?.status === 1) {
            try {
              // 3. Altera Status do GPL
              const resGpl: any = await firstValueFrom(this.sankhyaGateway.gplStatusAutorizado(nunota));
              if (resGpl?.status === '1' || resGpl?.status === 1) {
                this.mostrarToast(`✅ Pedido #${nunota} aprovado, nota confirmada, status GPL aprovado!`, 8000);
              } else {
                const msgErroConf = resGpl?.statusMessage || 'Erro desconhecido ao confirmar nota.';
                this.mostrarToast(`⚠️ Pedido #${nunota} aprovado e nota confirmada, mas erro ao mudar status GPL: ${msgErroConf}`, 10000);
              }
            } catch(errConf: any) {
              this.mostrarToast(`⚠️ Pedido #${nunota} aprovado e nota confirmada, mas erro ao mudar status GPL: ${errConf?.message}`, 10000);
            }
          } else {
            const msgErroConf = resConf?.statusMessage || 'Erro desconhecido ao confirmar nota.';
            this.mostrarToast(`⚠️ Pedido #${nunota} liberado, mas houve erro na confirmação: ${msgErroConf}`, 10000);
          }
        } catch(errConf: any) {
          this.mostrarToast(`⚠️ Pedido #${nunota} liberado, mas erro ao confirmar: ${errConf?.message}`, 10000);
        }

        this.itens = this.itens.filter(i => i.nunota !== nunota);
      } else {
        const msg = resLib?.statusMessage || bodyLib?.erro || 'Erro desconhecido.';
        this.mostrarToast(`Erro ao aprovar #${nunota}: ${msg}`);
      }
    } catch (err: any) {
      this.mostrarToast(`Erro ao aprovar #${nunota}: ${err?.message ?? 'Erro de comunicação.'}`);
    } finally {
      this.aprovando = null;
    }
  }

  /** Tira da tela o evento liberado (ou a nota inteira, quando não sobra evento ou nenhum foi indicado). */
  private removerRazaoOuItem(nunota: number, razao?: RazaoBloqueio | RazaoBloqueio[]): void {
    const item = this.itens.find(i => i.nunota === nunota);
    if (!item) return;
    if (razao) { const rem = Array.isArray(razao) ? razao : [razao]; item.razoes = item.razoes.filter(r => !rem.includes(r)); this.montarGrupos(item); }
    if (!razao || item.razoes.length === 0) this.itens = this.itens.filter(i => i.nunota !== nunota);
  }

  private modalRazao?: RazaoBloqueio | RazaoBloqueio[];
  private modalRazaoEscolhida?: RazaoBloqueio | RazaoBloqueio[];

  abrirModalReprovacao(nunota: number, razao?: RazaoBloqueio | RazaoBloqueio[]): void {
    this.modalRazao = razao;
    this.modalReprovacao = nunota;
    this.justificativa = '';
  }

  fecharModalReprovacao(): void {
    this.modalReprovacao = null;
    this.justificativa = '';
  }

  async confirmarReprovacao(): Promise<void> {
    const nunota = this.modalReprovacao;
    if (!nunota) return;
    this.modalRazaoEscolhida = this.modalRazao;

    if (!this.justificativa.trim()) {
      this.mostrarToast('Informe a justificativa antes de reprovar.');
      return;
    }

    this.reprovando = nunota;
    this.fecharModalReprovacao(); // Fecha a modal imediatamente para o usuário não ficar travado

    try {
      // 1. Encontra a nota e seus eventos (igual fizemos na aprovação)
      const item = this.itens.find(i => i.nunota === nunota);
      if (!item || !item.razoes || item.razoes.length === 0) {
        this.mostrarToast('Não foram encontrados bloqueios válidos para esta nota.');
        this.reprovando = null;
        return;
      }

      // 2. Monta o array com os dados de bloqueio (só o evento escolhido, se houver)
      const alvoRep = this.modalRazaoEscolhida ? (Array.isArray(this.modalRazaoEscolhida) ? this.modalRazaoEscolhida : [this.modalRazaoEscolhida]) : item.razoes;
      const itensLiberacao = alvoRep.map(r => ({
        nuChave: nunota,
        evento: r.evento,
        nucll: r.nucll,
        seqCascata: r.seqCascata,
        sequencia: r.sequencia,
        tabela: r.tabela
      }));

      // 3. Chama o serviço passando os itens e a justificativa
      const res: any = await firstValueFrom(
          this.sankhyaGateway.reprovarLiberacaoNativa(this.meuCodUsu, itensLiberacao, this.justificativa)
      );

      const body = res?.responseBody ?? res;

      // 4. Valida se a reprovação ocorreu com sucesso
      if (res?.status === '1' || res?.status === 1) {
        const resGpl: any = await firstValueFrom(this.sankhyaGateway.reprovarLiberacao(nunota, this.justificativa));
        if (resGpl?.status === '1' || resGpl?.status === 1) {
          this.mostrarToast(`🚫 Pedido #${nunota} reprovado com sucesso.`);
          // Remove da lista
          this.itens = this.itens.filter(i => i.nunota !== nunota);
        } else {
          const bodyGpl = resGpl?.responseBody ?? resGpl;
          const msg = resGpl?.statusMessage || bodyGpl?.erro || 'Erro desconhecido.';
          this.mostrarToast(`Erro ao reprovar #${nunota}: ${msg}`);
        }
      } else {
        const msg = res?.statusMessage || body?.erro || 'Erro desconhecido.';
        this.mostrarToast(`Erro ao reprovar #${nunota}: ${msg}`);
      }

    } catch (err: any) {
      this.mostrarToast(`Erro ao reprovar #${nunota}: ${err?.message ?? 'Erro de comunicação.'}`);
    } finally {
      this.reprovando = null;
    }
  }

  mostrarToast(msg: string, ms = 8000): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastMensagem = msg;
    this.toastVisivel = true;
    this.toastTimer = setTimeout(() => { this.toastVisivel = false; }, ms);
  }

  private atribuirControlesAutomaticos(nunota: number, msgAprovacao?: string): void {
    let msgSeriais = '';
    let msgLotes = '';
    let pendentes = 2;

    const concluir = () => {
      pendentes--;
      if (pendentes > 0) return;
      const partes = [msgSeriais, msgLotes].filter(m => !!m);
      if (partes.length > 0) {
        this.mostrarToast(`✅ Pedido #${nunota} aprovado!\n${partes.join('\n')}`, 12000);
      } else {
        this.mostrarToast(`✅ ${msgAprovacao ?? 'Pedido #' + nunota + ' aprovado com sucesso!'}`, 8000);
      }
      // Confirma o movimento no motor nativo do Sankhya (CACSP.confirmarNota) DEPOIS
      // de seriais/lotes atribuídos — é essa chamada que numera a nota (Nro. Nota),
      // monta a reserva de estoque conforme a TOP e gera o financeiro. Sem ela, o
      // pedido fica AUTORIZADO no GPL mas sem Nro. Nota, travando o faturamento
      // depois (ORA-20101 "Reserva diferente da definicao na TOP").
      this.confirmarMovimentoNativo(nunota);
    };

    this.serialService.atribuirSeriaisAutomaticos(nunota).subscribe({
      next: (res: any) => {
        const b = res?.responseBody ?? res;
        if (b?.sucesso === false) {
          msgSeriais = `Aviso seriais: ${b?.erro ?? 'Erro desconhecido'}`;
        } else {
          const lista = (b?.atribuidos ?? []).map((s: any) => s.controle ?? s.serie).filter(Boolean);
          if (lista.length) msgSeriais = `Seriais: ${lista.join(', ')}`;
          const avisos = (b?.avisos ?? []).map((a: any) => a.aviso).filter(Boolean);
          if (avisos.length) msgSeriais += (msgSeriais ? ' | ' : '') + `Sem estoque: ${avisos.join('; ')}`;
        }
        concluir();
      },
      error: () => { msgSeriais = 'Aviso: não foi possível atribuir seriais.'; concluir(); }
    });

    this.loteService.atribuirLotesAutomaticos(nunota).subscribe({
      next: (res: any) => {
        const b = res?.responseBody ?? res;
        if (b?.sucesso === false) {
          msgLotes = `Aviso lotes: ${b?.erro ?? 'Erro desconhecido'}`;
        } else {
          const lista = (b?.atribuidos ?? []).map((l: any) => l.controle).filter(Boolean);
          if (lista.length) msgLotes = `Lotes: ${lista.join(', ')}`;
          const avisos = (b?.avisos ?? []).map((a: any) => a.aviso).filter(Boolean);
          if (avisos.length) msgLotes += (msgLotes ? ' | ' : '') + `Sem estoque: ${avisos.join('; ')}`;
        }
        concluir();
      },
      error: () => { msgLotes = 'Aviso: não foi possível atribuir lotes.'; concluir(); }
    });
  }

  /**
   * Confirma o pedido de venda no motor nativo do Sankhya (CACSP.confirmarNota)
   * — o mesmo serviço que a tela nativa usa no botão "Confirmar". É essa chamada
   * que efetivamente numera a nota (Nro. Nota), monta a reserva de estoque
   * conforme a TOP e gera os títulos financeiros.
   */
  private confirmarMovimentoNativo(nunota: number): void {
    this.sankhyaGateway.confirmarMovimentoPedidoVenda(nunota).subscribe({
      next: (res: any) => {
        const body = res?.responseBody ?? res;
        if (body?.sucesso === true || body?.sucesso === 'true') {
          this.mostrarToast(`📄 Pedido #${nunota} confirmado — Nro. Nota gerado.`, 8000);
        } else {
          const msg = body?.erro ?? 'Erro desconhecido ao confirmar a nota.';
          this.mostrarToast(
            `⚠️ Pedido #${nunota} aprovado, mas houve falha ao gerar o Nro. Nota: ${msg}`,
            15000
          );
        }
      },
      error: (err: any) => {
        const msg = err?.error?.erro ?? err?.message ?? 'Erro de comunicação.';
        this.mostrarToast(
          `⚠️ Pedido #${nunota} aprovado, mas houve falha ao gerar o Nro. Nota: ${msg}`,
          15000
        );
      }
    });
  }

  fmtBrl(v: number): string {
    return (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  fmtDoc(doc: string): string {
    if (!doc) return '—';
    const d = doc.replace(/\D/g, '');
    if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    return doc;
  }
}
